import React, { useState } from "react";
import { ArrowLeft, Users, Calendar } from "lucide-react";
import { DetailContainer, BackButton } from "./UI";
import { toggleCommunityMembership } from "../services/meetlyService";
import { useUser } from "../context/user";
import styles from "./Communities.module.css";

// Detail view for a single community: shows its banner, description, and a
// join/leave toggle that optimistically updates local member count + the
// user's own joinedCommunities list before the Firestore write resolves.
export default function CommunityDetailScreen({ community, onBack }) {
  const { userData, setUserData } = useUser();
  const userId = userData?.uid || userData?.id;
  const joinedList = userData?.joinedCommunities || [];
  const isJoined = joinedList.includes(community.id);

  const [joined, setJoined] = useState(isJoined);
  const [loadingJoin, setLoadingJoin] = useState(false);
  const [toastMessage, setToastMessage] = useState(null);

  // Extract base member count (e.g., "45 members" -> 45)
  const baseCount =
    typeof community.members === "number"
      ? community.members
      : parseInt(community.members) || 12;

  const [memberCount, setMemberCount] = useState(baseCount);

  // Handler: Toggle membership status in Firestore and update local user state
  const handleToggleJoin = async () => {
    if (!userId) {
      alert("User session not found. Please log in again.");
      return;
    }

    setLoadingJoin(true);
    try {
      const willJoin = !joined;

      await toggleCommunityMembership(userId, community.id, willJoin);

      let updatedJoinedCommunities;
      if (willJoin) {
        updatedJoinedCommunities = [...joinedList, community.id];
        setJoined(true);
        setMemberCount((prev) => prev + 1);
        setToastMessage(`Successfully joined ${community.name}! 🎉`);
      } else {
        updatedJoinedCommunities = joinedList.filter(
          (id) => id !== community.id
        );
        setJoined(false);
        setMemberCount((prev) => Math.max(0, prev - 1));
        setToastMessage(`You left ${community.name}`);
      }

      if (setUserData) {
        setUserData((prev) => ({
          ...prev,
          joinedCommunities: updatedJoinedCommunities,
        }));
      }

      setTimeout(() => setToastMessage(null), 3000);
    } catch (error) {
      console.error("Error updating community membership:", error);
      alert("Failed to update membership. Check console for details.");
    } finally {
      setLoadingJoin(false);
    }
  };

  return (
    <DetailContainer>
      {toastMessage && (
        <div className={styles.toastNotification}>
          <span>{toastMessage}</span>
        </div>
      )}

      <div className={styles.topBar}>
        <BackButton onClick={onBack}>
          <ArrowLeft size={20} />
        </BackButton>
        <span className={styles.categoryBadge}>Community Hub</span>
      </div>

      <div className={styles.heroBanner}>
        <img
          src={
            community.image ||
            "https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=500&auto=format&fit=crop&q=60"
          }
          alt={community.name}
          className={styles.heroImage}
        />
      </div>

      <div className={styles.contentContainer}>
        <div className={styles.headerSection}>
          <h1 className={styles.eventTitle}>{community.name}</h1>
          <p className={styles.eventDateText}>
            <Users size={14} className={styles.inlineIcon} /> {memberCount}{" "}
            members
          </p>
        </div>

        <div className={styles.attendeesAndJoinRow}>
          <div className={styles.communityBadgeInfo}>
            <span>Dublin Community</span>
          </div>
          <button
            onClick={handleToggleJoin}
            disabled={loadingJoin}
            className={`${styles.compactJoinBtn} ${
              joined ? styles.joinedActive : ""
            }`}
          >
            {loadingJoin
              ? "Updating..."
              : joined
              ? "✓ Joined Community"
              : "+ Join Community"}
          </button>
        </div>

        <div className={styles.descriptionSection}>
          <h3 className={styles.descriptionTitle}>About this community</h3>
          <p className={styles.descriptionText}>
            {community.desc ||
              "Connect with like-minded people in Dublin, share experiences, and join our regular meetups."}
          </p>
        </div>

        <div className={styles.detailsCard}>
          <div className={styles.clickableAddressRow}>
            <div className={styles.detailIconWrapperHover}>
              <Calendar size={16} />
            </div>
            <span className={styles.addressText}>
              Regular meetups & social hangouts
            </span>
          </div>
        </div>
      </div>
    </DetailContainer>
  );
}
